import {
  gdaScore,
  gdaScoreCurated,
  gdaScoreClinicalTrials,
  gdaScoreInferred,
  gdaScoreModels,
  gdaScoreLiterature,
} from '../../AboutHelpers.tsx';
import { FormulaAccordion } from './components/FormulaAccordion';
import { ScoreComponentsVisual, ScoreComponent } from './components/ScoreComponentsVisual';
import { WeightStepGrid } from './components/WeightStepGrid';

const GDA_COMPONENTS: ScoreComponent[] = [
  {
    key: 'curated',
    label: 'Curated',
    maxWeight: 0.7,
    hex: '#0a104f',
    textHex: '#0a104f',
    sources: ['CLINGEN', 'CLINPGX', 'CLINVAR', 'GENCC', 'MGD', 'ORPHANET', 'PSYGENET', 'UNIPROT', '…'],
    weightSteps: [
      { condition: '|S_C| > 4 sources', weight: 0.7 },
      { condition: '|S_C| = 4 sources', weight: 0.6 },
      { condition: '|S_C| = 3 sources', weight: 0.5 },
      { condition: '|S_C| = 2 sources', weight: 0.45 },
      { condition: '|S_C| = 1 source', weight: 0.4 },
      { condition: '|S_C| = 0 sources', weight: 0.0 },
    ],
  },
  {
    key: 'clinical',
    label: 'Clinical Trials',
    maxWeight: 0.1,
    hex: '#be0080',
    textHex: '#be0080',
    sources: ['CLINICALTRIALS'],
    weightSteps: [
      { condition: '|S_T| > 0 sources', weight: 0.1 },
      { condition: '|S_T| = 0 sources', weight: 0.0 },
    ],
  },
  {
    key: 'inferred',
    label: 'Inferred',
    maxWeight: 0.25,
    hex: '#8B5CF6',
    textHex: '#6D28D9',
    sources: ['FINNGEN', 'GWASCAT', 'HPO', 'PHEWASCAT', 'UKBIOBANK'],
    weightSteps: [
      { condition: '|S_I| ≥ 3 sources', weight: 0.25 },
      { condition: '|S_I| = 2 sources', weight: 0.15 },
      { condition: '|S_I| = 1 source', weight: 0.05 },
      { condition: '|S_I| = 0 sources', weight: 0.0 },
    ],
  },
  {
    key: 'models',
    label: 'Models',
    maxWeight: 0.1,
    hex: '#10B981',
    textHex: '#047857',
    sources: ['MGD MOUSE', 'RGD RAT', 'TEXTMINING MODELS'],
    weightSteps: [
      { condition: '|S_M| > 1 source', weight: 0.1 },
      { condition: '|S_M| = 1 source', weight: 0.05 },
      { condition: '|S_M| = 0 sources', weight: 0.0 },
    ],
  },
  {
    key: 'literature',
    label: 'Literature',
    maxWeight: 0.4,
    hex: '#B45309',
    textHex: '#B45309',
    sources: ['TEXTMINING HUMAN', 'TEXTMINING MODELS'],
    weightSteps: [
      { condition: 'N_Pubs > 99', weight: 0.4 },
      { condition: '9 < N_Pubs ≤ 99', weight: 0.3 },
      { condition: '1 < N_Pubs ≤ 9', weight: 0.2 },
      { condition: 'N_Pubs = 1', weight: 0.1 },
      { condition: 'N_Pubs = 0', weight: 0.0 },
    ],
  },
];

function AboutMetricsGdaScore(): JSX.Element {
  return (
    <div id="gdaScore" className="mt-10">
      <h3>GDA Score</h3>
      <p>
        The DISGENET Score (S<sub style={{ fontSize: 'smaller', marginLeft: 2 }}>GDA</sub>) for GDAs is
        computed according to the weighted sum of five evidence components. Hover over any value to see how it
        scales with the number of supporting sources.
      </p>
      <ScoreComponentsVisual
        components={GDA_COMPONENTS}
        note="Values (≤ x.xx) are the maximum each component can contribute. Hover to see the full scale."
      />
      <FormulaAccordion label="View formula details">
        <WeightStepGrid components={GDA_COMPONENTS} />
        <span>{gdaScore()}</span>
        <br />
        <span>{gdaScoreCurated()}</span>
        <br />
        <span>{gdaScoreClinicalTrials()}</span>
        <br />
        <span>{gdaScoreInferred()}</span>
        <br />
        <span>{gdaScoreModels()}</span>
        <br />
        <span>{gdaScoreLiterature()}</span>
      </FormulaAccordion>
    </div>
  );
}

export default AboutMetricsGdaScore;
